
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { CopilotAssistant } from '@/hooks/ai-agents/types/database-types';

interface AssistantAvatarGroupProps {
  assistants: CopilotAssistant[];
  maxVisible?: number;
  size?: 'sm' | 'md';
  className?: string;
}

const AssistantAvatarGroup: React.FC<AssistantAvatarGroupProps> = ({
  assistants,
  maxVisible = 3,
  size = 'sm',
  className
}) => {
  const visibleAssistants = assistants.slice(0, maxVisible);
  const remainingCount = Math.max(0, assistants.length - maxVisible);
  
  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-sm'
  };
  
  const sizeClass = sizeClasses[size];

  if (assistants.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center", className)}>
      <div className="flex -space-x-1">
        {visibleAssistants.map((assistant, index) => (
          <motion.div
            key={assistant.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            className="relative"
          >
            {assistant.profile_picture_url ? (
              <img
                src={assistant.profile_picture_url}
                alt={assistant.name}
                className={cn(
                  "rounded-full border-2 border-white dark:border-slate-800 object-cover",
                  "shadow-sm hover:scale-110 transition-transform duration-200",
                  sizeClass
                )}
                title={assistant.name}
              />
            ) : (
              <div
                className={cn(
                  "rounded-full border-2 border-white dark:border-slate-800",
                  "bg-gradient-to-br from-blue-500 to-purple-600",
                  "flex items-center justify-center text-white font-medium",
                  "shadow-sm hover:scale-110 transition-transform duration-200",
                  sizeClass
                )}
                title={assistant.name}
              >
                {assistant.name.charAt(0).toUpperCase()}
              </div>
            )}
          </motion.div>
        ))}
        
        {remainingCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: visibleAssistants.length * 0.05 }}
            className={cn(
              "rounded-full border-2 border-white dark:border-slate-800",
              "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
              "flex items-center justify-center font-medium",
              "shadow-sm",
              sizeClass
            )}
            title={`+${remainingCount} more assistant${remainingCount > 1 ? 's' : ''}`}
          >
            +{remainingCount}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AssistantAvatarGroup;
